import _ from 'lodash';
import { arrayPush } from 'redux-form';

import { NotificationTheme } from '../../../../constants/notification';
import { NotificationsTranslationKeys } from '../../../../containers/notifications/notifications-keys';
import { Dispatch, GetState } from '../../../../redux/redux.types';
import { getSelectedOrganizationId } from '../../../../selectors/organizations/organizations';
import { organizationService } from '../../../../transport';
import { teamMembersService } from '../../../../transport/services/team-members-service';
import {
  Response,
  TransportResponse,
} from '../../../../transport/transport.types';
import { errorLogger } from '../../../../utils/error-logger/error-logger';
import { BI_ERRORS } from '../../../../utils/statistics/bi-error-logger/bi-errors';

import {
  closeTeamMemberAddModal,
  closeTeamMemberEditModal,
} from '../../team.actions';
import { TeamMemberFormAccessPermissions } from '../../team.constants';
import { getEditingTeamMemberId } from '../../team.selectors-ui';
import { TeamMembersData } from '../../team.types';
import { TEAM_MEMBER_EDIT_FORM_NAME } from '../team-member-edit-form/team-member-edit-form.container';
import { TEAM_MEMBER_ADD_FORM_NAME } from './team-member-form';
import { PROJECT_OVERVIEW_MEMBERS_ACTION_NAMES } from '../../../project-overview/project-overview-members/project-overview-members.actions';

interface AddTeamMemberData {
  email: string;
  securityGroups: string[];
}

enum TeamMemberBackendErrorCodes {
  MemberAlreadyExist = 409,
}

const isMemberAlreadyExist = (response: Response<null>) => {
  return (
    response.code === TeamMemberBackendErrorCodes.MemberAlreadyExist &&
    response.message === 'already a member'
  );
};

export const TEAM_MEMBER_FORM_ACTION_NAMES = {
  ADD_MEMBERS_SUCCESS: 'ADD_TEAM_MEMBER_FORM_ACTION_NAMES__ADD_MEMBERS_SUCCESS',
  ADD_MEMBERS_ERROR: 'ADD_TEAM_MEMBER_FORM_ACTION_NAMES__ADD_MEMBERS_ERROR',
  ADD_MEMBERS_USER_ALREADY_EXISTS_ERROR:
    'ADD_TEAM_MEMBER_FORM_ACTION_NAMES__ADD_MEMBERS_USER_ALREADY_EXISTS_ERROR',

  ADD_MEMBER_SUCCESS: 'ADD_TEAM_MEMBER_FORM_ACTION_NAMES__ADD_MEMBER_SUCCESS',
  ADD_MEMBER_ERROR: 'ADD_TEAM_MEMBER_FORM_ACTION_NAMES__ADD_MEMBER_ERROR',

  EDIT_MEMBER_SUCCESS: 'ADD_TEAM_MEMBER_FORM_ACTION_NAMES__EDIT_MEMBER_SUCCESS',
  EDIT_MEMBER_ERROR: 'ADD_TEAM_MEMBER_FORM_ACTION_NAMES__EDIT_MEMBER_ERROR',
};

export const addTeamMemberAccess =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(
      arrayPush(TEAM_MEMBER_ADD_FORM_NAME, 'securityGroups', {
        level: undefined,
        permissions: TeamMemberFormAccessPermissions.Viewer,
        projectsIds: [],
      }),
    );
  };

export const addTeamMemberSuccess = (member: AddTeamMemberData) => ({
  type: PROJECT_OVERVIEW_MEMBERS_ACTION_NAMES.SEND_PROJECT_INVITATION_SUCCESS,
  payload: member,
});

const addTeamMemberError = (reason) => ({
  type: TEAM_MEMBER_FORM_ACTION_NAMES.ADD_MEMBER_ERROR,
  payload: reason,
});

const addTeamMembersSuccess = (amount: number) => ({
  type: TEAM_MEMBER_FORM_ACTION_NAMES.ADD_MEMBERS_SUCCESS,
  payload: undefined,
  notification: {
    key: NotificationsTranslationKeys.teamAddMembersSuccess,
    theme: NotificationTheme.Success,
    options: {
      amount,
    },
  },
});

const addTeamMembersError = (amount: number) => ({
  type: TEAM_MEMBER_FORM_ACTION_NAMES.ADD_MEMBERS_ERROR,
  payload: undefined,
  notification: {
    key: NotificationsTranslationKeys.teamAddMembersError,
    theme: NotificationTheme.Error,
    options: {
      amount,
    },
  },
});

const addTeamMembersUserAlreadyExistsError = (amount: number) => ({
  type: TEAM_MEMBER_FORM_ACTION_NAMES.ADD_MEMBERS_USER_ALREADY_EXISTS_ERROR,
  payload: undefined,
  notification: {
    key: NotificationsTranslationKeys.teamAddMembersUserAlreadyExistsError,
    theme: NotificationTheme.Error,
    options: {
      amount,
    },
  },
});

const editTeamMemberSuccess = (payload: {
  memberId: string;
  securityGroups: string[];
}) => ({
  type: TEAM_MEMBER_FORM_ACTION_NAMES.EDIT_MEMBER_SUCCESS,
  payload,
  notification: {
    key: NotificationsTranslationKeys.teamMemberEditSuccess,
    theme: NotificationTheme.Success,
  },
});

const editTeamMemberError = (reason) => ({
  type: TEAM_MEMBER_FORM_ACTION_NAMES.EDIT_MEMBER_ERROR,
  payload: reason,
  notification: {
    key: NotificationsTranslationKeys.teamMemberEditError,
    theme: NotificationTheme.Error,
  },
});

export const addTeamMembers =
  ({ emails, securityGroups }: TeamMembersData) =>
  (dispatch: Dispatch, getState: GetState): Promise<void> => {
    const state = getState();

    const organizationId = getSelectedOrganizationId(state);

    return Promise.all(
      _.map(emails, (email: string) => {
        return organizationService
          .inviteOrganizationMember({
            organizationId,
            member: { email, securityGroups },
          })
          .then(
            () => {
              dispatch(addTeamMemberSuccess({ email, securityGroups }));
            },
            ({ response }) => {
              dispatch(addTeamMemberError(_.get(response, 'data')));

              errorLogger.logError({
                errorDescription: BI_ERRORS.teamMembers.addTeamMemberFailed,
                otherParams: { response },
              });

              return Promise.reject(response);
            },
          );
      }),
    ).then(
      () => {
        dispatch(closeTeamMemberAddModal());
        dispatch(addTeamMembersSuccess(_.size(emails)));
      },
      (response: TransportResponse<null>) => {
        const responseData = _.get(response, 'data', {
          code: 400,
          message: '',
          payload: null,
        });

        if (isMemberAlreadyExist(responseData)) {
          dispatch(addTeamMembersUserAlreadyExistsError(_.size(emails)));
          return;
        }

        dispatch(addTeamMembersError(_.size(emails)));
      },
    );
  };

export const addTeamMemberEditFormAccess =
  () =>
  (dispatch: Dispatch): void => {
    dispatch(
      arrayPush(TEAM_MEMBER_EDIT_FORM_NAME, 'securityGroups', {
        level: undefined,
        permissions: TeamMemberFormAccessPermissions.Viewer,
        projectsIds: [],
      }),
    );
  };

export const editTeamMember =
  (securityGroups: string[]) => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const organizationId = getSelectedOrganizationId(state);
    const memberId = getEditingTeamMemberId(state);

    return teamMembersService
      .editTeamMember({ memberId, organizationId, securityGroups })
      .then(
        () => {
          dispatch(closeTeamMemberEditModal());
          dispatch(editTeamMemberSuccess({ memberId, securityGroups }));
        },
        ({ response }) => {
          dispatch(editTeamMemberError(_.get(response, 'data')));

          errorLogger.logError({
            errorDescription: BI_ERRORS.teamMembers.editTeamMemberFailed,
            otherParams: { response },
          });

          return Promise.reject(response);
        },
      );
  };
